// Generate unique QR code value for line items
export const generateLineItemQRCode = (registrationId: string, lineItemId: string): string | null => {
  if (!registrationId || !lineItemId) {
    console.warn('Skipping QR generation - missing required fields:', { registrationId, lineItemId });
    return null;
  }
  const randomHash = crypto.randomUUID().slice(0, 8);
  return `${registrationId}-${lineItemId}-${randomHash}`;
};

// Generate QR code for ticket check-in/redemption
export const generateTicketQRCode = (qrCodeValue: string): string => {
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrCodeValue)}&size=200x200`;
};

// Backfill existing line items with QR codes
export const backfillLineItemQRCodes = async (supabase: any): Promise<number> => {
  try {
    // Get all line items without QR codes
    const { data: lineItemsWithoutQR, error: fetchError } = await supabase
      .from('event_registration_line_items')
      .select('id, registration_id')
      .is('qr_code_value', null);

    if (fetchError) {
      console.error('❌ Failed to fetch line items without QR codes:', fetchError);
      return 0;
    }

    if (!lineItemsWithoutQR || lineItemsWithoutQR.length === 0) {
      console.log('✅ All line items already have QR codes');
      return 0;
    }

  // Filter out items with null registration_id before processing
  const validLineItems = lineItemsWithoutQR.filter(item => item.registration_id && item.id);
  const skippedCount = lineItemsWithoutQR.length - validLineItems.length;
  
  if (skippedCount > 0) {
    console.warn(`⚠️ Skipping ${skippedCount} line items with missing registration_id or id`);
  }

  if (validLineItems.length === 0) {
    console.log('✅ No valid line items to process');
    return 0;
  }

  console.log(`🔄 Backfilling QR codes for ${validLineItems.length} valid line items`);

  // Process items individually with detailed logging
  let successCount = 0;
  let errorCount = 0;

  for (const lineItem of validLineItems) {
    try {
      const qrCodeValue = generateLineItemQRCode(lineItem.registration_id, lineItem.id);
      if (!qrCodeValue) {
        console.error(`❌ Failed to generate QR code for item ${lineItem.id} - null value returned`);
        errorCount++;
        continue;
      }

      const { error } = await supabase
        .from('event_registration_line_items')
        .update({ qr_code_value: qrCodeValue })
        .eq('id', lineItem.id);
      
      if (error) {
        console.error(`❌ Failed to update item ${lineItem.id}:`, error);
        errorCount++;
      } else {
        console.log(`✅ Updated QR code for item ${lineItem.id}`);
        successCount++;
      }
    } catch (error) {
      console.error(`❌ Error processing item ${lineItem.id}:`, error);
      errorCount++;
    }
  }

  console.log(`✅ Successfully backfilled QR codes for ${successCount} line items` + 
    (errorCount > 0 ? ` (${errorCount} failed)` : ''));
  return successCount;
  } catch (error) {
    console.error('❌ Error during QR code backfill:', error);
    return 0;
  }
};

export const generateTeamQRCode = async (profileId: string): Promise<string> => {
  try {
    // Create the profile URL
    const profileUrl = `https://recruitlook.com/profile/${profileId}`;
    
    // Use online QR code generator for simplicity
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(profileUrl)}&size=100x100`;
    
    return qrImageUrl;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw error;
  }
};

export const generateQRCodeUrl = (data: string, size: number = 100): string => {
  // Fallback to online QR code generator
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data)}&size=${size}x${size}`;
};